import { visionLabApi } from '@/app/apiClient';
import type {
  CalibrationDevicePage,
  CalibrationDeviceSessionsPage,
  CalibrationDeviceStationBinding,
  CalibrationDiscardResult,
  CalibrationDownload,
  CalibrationFile,
  CalibrationFileUploadBeginResponse,
  CalibrationFileUploadProgressResponse,
  CalibrationRun,
  CalibrationSession,
  CalibrationSessionDetail,
  CalibrationStation,
  CalibrationStationPage,
  CreateCalibrationDeviceStationBindingInput,
  CreateCalibrationFileInput,
  CreateCalibrationRunInput,
  CreateCalibrationSessionInput,
  CreateCalibrationStationInput,
  DeleteCalibrationStationInput,
  DiscardCalibrationRunInput,
  UpdateCalibrationStationInput,
} from '@/features/calibration/types/calibration';
import type { CalibrationRunDiagnostics } from '@/features/calibration/types/calibrationDiagnostics';
import type { ApiCallOptions } from '@/shared/api/client';
import { commandOptions } from '@/shared/api/command';

const seg = (value: string) => encodeURIComponent(value);
export const fetchCalibrationStations = (
  params: { cursor?: string; limit?: number } = {},
  options: ApiCallOptions = {},
) =>
  visionLabApi.get<CalibrationStationPage>('/calibration-stations', {
    ...options,
    searchParams: params,
  });
export const createCalibrationStation = (
  input: CreateCalibrationStationInput,
  idempotencyKey: string,
) =>
  visionLabApi.post<CalibrationStation>('/calibration-stations', {
    ...commandOptions({ idempotencyKey }),
    json: input,
  });
export const patchCalibrationStation = (
  stationId: string,
  patch: UpdateCalibrationStationInput,
  idempotencyKey: string,
) =>
  visionLabApi.patch<CalibrationStation>(`/calibration-stations/${seg(stationId)}`, {
    ...commandOptions({ idempotencyKey }),
    json: patch,
  });
export const deleteCalibrationStation = (
  stationId: string,
  input: DeleteCalibrationStationInput,
  idempotencyKey: string,
) =>
  visionLabApi.delete<CalibrationStation>(`/calibration-stations/${seg(stationId)}`, {
    ...commandOptions({ idempotencyKey }),
    json: input,
  });
export const fetchCalibrationDevices = (
  params: { stationId?: string; cursor?: string; limit?: number } = {},
  options: ApiCallOptions = {},
) =>
  visionLabApi.get<CalibrationDevicePage>('/calibration-devices', {
    ...options,
    searchParams: params,
  });
export const createCalibrationDeviceStationBinding = (
  input: CreateCalibrationDeviceStationBindingInput,
  idempotencyKey: string,
) =>
  visionLabApi.post<CalibrationDeviceStationBinding>('/calibration-devices', {
    ...commandOptions({ idempotencyKey }),
    json: input,
  });
export const fetchCalibrationDeviceSessions = (
  deviceId: string,
  params: { cursor?: string; limit?: number } = {},
  options: ApiCallOptions = {},
) =>
  visionLabApi.get<CalibrationDeviceSessionsPage>(
    `/calibration-devices/${seg(deviceId)}/sessions`,
    { ...options, searchParams: params },
  );
export const createCalibrationSession = (
  deviceId: string,
  input: CreateCalibrationSessionInput,
  idempotencyKey: string,
) =>
  visionLabApi.post<CalibrationSession>(`/calibration-devices/${seg(deviceId)}/sessions`, {
    ...commandOptions({ idempotencyKey }),
    json: input,
  });
export const fetchCalibrationSession = (sessionId: string, options: ApiCallOptions = {}) =>
  visionLabApi.get<CalibrationSessionDetail>(`/calibration-sessions/${seg(sessionId)}`, options);
export const fetchCalibrationRunDiagnostics = (runId: string, options: ApiCallOptions = {}) =>
  visionLabApi.get<CalibrationRunDiagnostics>(
    `/calibration-runs/${seg(runId)}/diagnostics`,
    options,
  );
export const createCalibrationFile = (
  sessionId: string,
  input: CreateCalibrationFileInput,
  idempotencyKey: string,
  options: ApiCallOptions = {},
) =>
  visionLabApi.post<CalibrationFileUploadBeginResponse>(
    `/calibration-sessions/${seg(sessionId)}/files`,
    { ...commandOptions({ idempotencyKey, signal: options.signal }), json: input },
  );
export const completeCalibrationFile = (
  fileId: string,
  idempotencyKey: string,
  options: ApiCallOptions = {},
) =>
  visionLabApi.post<CalibrationFile>(`/calibration-files/${seg(fileId)}/complete`, {
    ...commandOptions({ idempotencyKey, signal: options.signal }),
    json: {},
  });
export const beginCalibrationFileResumableUpload = (
  fileId: string,
  contentHash: string,
  options: ApiCallOptions = {},
) =>
  visionLabApi.post<CalibrationFileUploadProgressResponse>(
    `/calibration-files/${seg(fileId)}/resumable-begin`,
    { ...options, json: { contentHash } },
  );
export const fetchCalibrationFileUploadOffset = (fileId: string, options: ApiCallOptions = {}) =>
  visionLabApi.get<CalibrationFileUploadProgressResponse>(
    `/calibration-files/${seg(fileId)}/upload-offset`,
    options,
  );
export const restartCalibrationFileUpload = (
  fileId: string,
  contentHash: string,
  options: ApiCallOptions = {},
) =>
  visionLabApi.post<CalibrationFileUploadProgressResponse>(
    `/calibration-files/${seg(fileId)}/restart-upload`,
    { ...options, json: { contentHash } },
  );
export const cancelCalibrationFileUpload = (
  fileId: string,
  idempotencyKey: string,
  options: ApiCallOptions = {},
) =>
  visionLabApi.post<CalibrationFile>(`/calibration-files/${seg(fileId)}/cancel`, {
    ...commandOptions({ idempotencyKey, signal: options.signal }),
    json: {},
  });
export const createCalibrationRun = (
  sessionId: string,
  input: CreateCalibrationRunInput,
  idempotencyKey: string,
) =>
  visionLabApi.post<CalibrationRun>(`/calibration-sessions/${seg(sessionId)}/runs`, {
    ...commandOptions({ idempotencyKey }),
    json: input,
  });
export const discardCalibrationRun = (
  runId: string,
  input: DiscardCalibrationRunInput,
  idempotencyKey: string,
) =>
  visionLabApi.post<CalibrationDiscardResult>(`/calibration-runs/${seg(runId)}/discard`, {
    ...commandOptions({ idempotencyKey }),
    json: input,
  });
export const downloadCalibrationFile = (fileId: string) =>
  visionLabApi.post<CalibrationDownload>(`/calibration-files/${seg(fileId)}/download-url`, {
    json: {},
  });
